import { observer } from 'mobx-react-lite';
import queryString from 'query-string';
import React, { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Container, Form, Grid, Header, Message, Segment } from 'semantic-ui-react';

import { history } from '../..';
import LoadingComponent from '../../app/layout/LoadingComponent';
import { LoginValuesEmailBirthday } from '../../app/models/login';
import { RootStoreContext } from '../../app/stores/rootStore';

const SmTownMvAuth: React.FC<RouteComponentProps> = ({ location }) => {
  const rootStore = useContext(RootStoreContext);
  const { setSession } = rootStore.sessionStore;
  const { loginSmTownMvAuth } = rootStore.memberStore;

  const { client_id, callback_url, auth_params, fanclub, id_text, pass_text } = queryString.parse(location.search);

  const [appLoaded, setAppLoaded] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState(new LoginValuesEmailBirthday());

  // IMEコンポジション中（全角入力中）かどうかを管理するフラグ
  const isComposing = useRef(false);
  const toastIdRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      (async () => {
        const session = await setSession({
          session_id: '',
          client_id: client_id as string,
          auth_params: auth_params as string,
          callback_url: callback_url as string,
        });
        setSessionId(session.session_id);
        setAppLoaded(true);
      })();
    } catch (error) {
      history.push('/error');
    }
  }, [auth_params, callback_url, client_id, setSession]);

  if (!appLoaded) return <LoadingComponent content="Loading..." />;

  const handleInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    if (name === 'birthday') {
      // IMEコンポジション中は入力値をそのまま表示（全角数字入力中の表示対応）
      if (isComposing.current) {
        setFormValues({ ...formValues, birthday: value });
        return;
      }
      // 半角数字以外を除去（最大8桁）
      const filtered = value.replace(/[^0-9]/g, '').slice(0, 8);
      setFormValues({ ...formValues, birthday: filtered });
    } else {
      setFormValues({ ...formValues, [name]: value });
    }
  };

  // IMEコンポジション開始（全角入力開始）
  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  // IMEコンポジション終了後、全角文字を除去して反映
  const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
    isComposing.current = false;
    // コンポジション終了時に全角数字を半角数字に変換し、非数字文字を除去
    const value = (event.target as HTMLInputElement).value;
    // 全角数字を半角数字に変換
    const halfWidth = value.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
    // 半角数字以外を除去（最大8桁）
    const filtered = halfWidth.replace(/[^0-9]/g, '').slice(0, 8);
    setFormValues({ ...formValues, birthday: filtered });
  };

  const handleSubmit = async () => {
    // 既存のトーストをクリアして最新のエラーのみ表示
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
    }

    // メールアドレスバリデーション
    if (!formValues.email || !formValues.email.includes('@')) {
      toastIdRef.current = String(toast.error('有効なメールアドレスを入力してください。'));
      return;
    }

    // 生年月日バリデーション（8桁半角数字）
    if (!formValues.birthday || formValues.birthday.length !== 8) {
      toastIdRef.current = String(toast.error('生年月日は8桁の半角数字で入力してください。（例：20200101）'));
      return;
    }

    setSubmitting(true);

    const values = {
      ...formValues,
      session_id: sessionId,
      client_id: client_id as string,
      auth_params: auth_params as string,
    };

    try {
      await loginSmTownMvAuth(values);
    } catch (error: any) {
      const data = error.response?.data;

      // 既存のトーストをクリアして最新のエラーのみ表示
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }

      // 継続回数エラーを優先的にチェック
      if (data?.errorCode === 'CONTINUOUS_COUNT_ERROR' || data?.message?.includes('Continuous count')) {
        toastIdRef.current = String(toast.error('ファンクラブ継続年数(継続回数)の条件を満たしていません。'));
      } else if (data?.message === 'User locked') {
        toastIdRef.current = String(toast.error('アカウントがロックされました。時間を空けてから再度お試しください。'));
      } else if (data?.message === 'Invalid request body' || data?.message === 'Invalid credentials') {
        const errorMessage = 'ファンクラブ認証に失敗しました。';
        data.errorCode
          ? (toastIdRef.current = String(toast.error(`${errorMessage}(${data.errorCode})`)))
          : (toastIdRef.current = String(toast.error(errorMessage)));
      } else {
        toastIdRef.current = String(toast.error('ファンクラブ認証に失敗しました。'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const titleText = '';
  const descriptionText = fanclub
    ? `${fanclub}`
    : 'Weverse Accountにご登録のメールアドレスとファンクラブにご登録の生年月日(8桁)を入力してください。';
  const emailPlaceholder = id_text ? String(id_text) : 'Weverse Accountにご登録のメールアドレス';
  const birthdayPlaceholder = pass_text ? String(pass_text) : 'ファンクラブにご登録の生年月日(例：20200101)';

  return (
    <Container>
      <Grid textAlign="center">
        <Grid.Column textAlign="center" style={{ width: '100%', maxWidth: 450, minWidth: 350 }}>
          <Segment>
            {titleText && (
              <Header as="h3" textAlign="center">
                <div>{titleText}</div>
              </Header>
            )}
            {descriptionText && (
              <Message size="small">
                <div>Weverse Accountにご登録のメールアドレスと</div>
                <div>ファンクラブにご登録の生年月日(8桁)を入力してください。</div>
              </Message>
            )}
            <Form size="large" onSubmit={handleSubmit}>
              <Form.Input
                fluid
                icon="mail"
                iconPosition="left"
                placeholder={emailPlaceholder}
                name="email"
                type="email"
                value={formValues.email}
                onChange={handleInputChange}
                input={{ style: { fontSize: '15px' } }}
              />
              <Form.Input
                fluid
                icon="calendar"
                iconPosition="left"
                placeholder={birthdayPlaceholder}
                name="birthday"
                type="text"
                inputMode="numeric"
                value={formValues.birthday}
                onChange={handleInputChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                input={{ style: { fontSize: '15px' } }}
              />
              <Button fluid size="large" type="submit" disabled={submitting} loading={submitting}>
                認証する
              </Button>
            </Form>
          </Segment>
        </Grid.Column>
      </Grid>
    </Container>
  );
};

export default observer(SmTownMvAuth);
